/* ─── Layout components: Row, Column, List, SideBySide, Paginated, Combined ─── */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { parseColor } from '../colors';

// ─── Justify / Align / Spacing maps ────────────────────────────────────

const SPACING: Record<string, number> = {
  tight: 4,
  normal: 8,
  loose: 16,
};

const JUSTIFY: Record<string, string> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  'space-between': 'space-between',
  'space-around': 'space-around',
  'space-evenly': 'space-evenly',
};

// ─── Row ────────────────────────────────────────────────────────────────

export function ArtifactRow({
  children,
  justify = 'start',
  align = 'center',
  spacing = 'loose',
}: {
  children?: ReactNode[];
  justify?: string;
  align?: string;
  spacing?: string;
}) {
  const items = children ?? [];

  return (
    <div
      className="a-row"
      style={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: JUSTIFY[justify] ?? 'flex-start',
        alignItems: align,
        gap: SPACING[spacing] ?? 16,
        flexWrap: 'wrap',
      }}
    >
      {items}
    </div>
  );
}

// ─── Column ─────────────────────────────────────────────────────────────

export function ArtifactColumn({
  children,
  justify = 'start',
  align = 'stretch',
  spacing = 'normal',
}: {
  children?: ReactNode[];
  justify?: string;
  align?: string;
  spacing?: string;
}) {
  const items = children ?? [];

  return (
    <div
      className="a-column"
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: JUSTIFY[justify] ?? 'flex-start',
        alignItems: align,
        gap: SPACING[spacing] ?? 8,
      }}
    >
      {items}
    </div>
  );
}

// ─── SideBySide ────────────────────────────────────────────────────────

export function ArtifactSideBySide({
  lhs,
  rhs,
  align = 'center',
}: {
  lhs?: ReactNode;
  rhs?: ReactNode;
  align?: string;
}) {
  return (
    <div
      className="a-side-by-side"
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: align,
        gap: 'var(--artifact-gap, 16px)',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>{lhs}</div>
      <div style={{ flex: 1, minWidth: 0 }}>{rhs}</div>
    </div>
  );
}

// ─── List (template-based repeating) ────────────────────────────────────

export function ArtifactList({
  data,
  template,
  max_items,
}: {
  data?: unknown[];
  template?: ReactNode;
  max_items?: number;
}) {
  if (!Array.isArray(data) || !template) {
    return (
      <div className="a-list-empty" style={{ color: 'var(--artifact-foreground)', opacity: 0.5 }}>
        No data
      </div>
    );
  }

  const items = max_items != null ? data.slice(0, max_items) : data;

  return (
    <div
      className="a-list"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--artifact-gap, 16px)',
      }}
    >
      {items.map((_, index) => (
        <div key={index}>{template}</div>
      ))}
    </div>
  );
}

// ─── Paginated ─────────────────────────────────────────────────────────

export function ArtifactPaginated({
  children,
  page_size = 5,
}: {
  children?: ReactNode[];
  page_size?: number;
}) {
  const items = children ?? [];
  const pageCount = Math.max(1, Math.ceil(items.length / page_size));
  const [page, setPage] = useState(0);
  const [following, setFollowing] = useState(true);
  const prevLenRef = useRef(items.length);

  // Track whether we're following the tail
  useEffect(() => {
    const prevLen = prevLenRef.current;
    prevLenRef.current = items.length;
    if (items.length > prevLen && following) {
      setPage(pageCount - 1);
    }
  }, [items.length, page_size, pageCount, following]);

  const clampedPage = Math.min(page, pageCount - 1);

  // When following, show the last page_size items (sliding window);
  // otherwise use page-aligned slicing
  const isOnLastPage = clampedPage >= pageCount - 1;
  const start = (following && isOnLastPage && items.length > page_size)
    ? items.length - page_size
    : clampedPage * page_size;
  const visible = items.slice(start, start + page_size);

  return (
    <div className="a-paginated" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--artifact-gap, 16px)' }}>
      <div className="a-paginated-items">
        {visible.map((child, i) => (
          <div key={start + i} className="a-paginated-item">{child}</div>
        ))}
      </div>
      {pageCount > 1 && (
        <div className="a-table-pagination">
          <button type="button" onClick={() => { setPage(0); setFollowing(false); }} disabled={clampedPage === 0}>
            ⏮
          </button>
          <button type="button" onClick={() => { setPage(clampedPage - 1); setFollowing(false); }} disabled={clampedPage === 0}>
            ◀
          </button>
          <span>{clampedPage + 1} / {pageCount}</span>
          <button type="button" onClick={() => { setPage(clampedPage + 1); if (clampedPage + 1 >= pageCount - 1) setFollowing(true); }} disabled={clampedPage >= pageCount - 1}>
            ▶
          </button>
          <button type="button" onClick={() => { setPage(pageCount - 1); setFollowing(true); }} disabled={clampedPage >= pageCount - 1}>
            ⏭
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Shared compass slot ───────────────────────────────────────────────

interface CompassProps {
  center?: ReactNode[];
  north?: ReactNode[];
  north_east?: ReactNode[];
  east?: ReactNode[];
  south_east?: ReactNode[];
  south?: ReactNode[];
  south_west?: ReactNode[];
  west?: ReactNode[];
  north_west?: ReactNode[];
}

function Slot({ children, style }: { children: ReactNode[]; style?: React.CSSProperties }) {
  if (!children || children.length === 0) return null;
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', ...style }}>
      {children.map((child, i) => <div key={i}>{child}</div>)}
    </div>
  );
}

// ─── Spacer ────────────────────────────────────────────────────────────

const SPACER_WIDTHS: Record<string, number | string> = {
  thumbnail: 70,
  small: 227,
  medium: 340,
  large: 680,
  full: '100%',
};

const SPACER_HEIGHTS: Record<string, number | string> = {
  thumbnail: 70,
  small: 128,
  medium: 192,
  large: 384,
  full: '100%',
};

// Resolve a size spec — either a preset name (`'medium'`, `'full'`, …)
// or a raw integer pixel value sent from the server — to a CSS dimension.
function resolveSizeSpec(spec: string | number, presets: Record<string, number | string>): number | string {
  if (typeof spec === 'number') return spec;
  return presets[spec] ?? '100%';
}

export function ArtifactSpacer({
  width = 'full',
  height = 'full',
}: {
  width?: string | number;
  height?: string | number;
}) {
  const w = resolveSizeSpec(width, SPACER_WIDTHS);
  const h = resolveSizeSpec(height, SPACER_HEIGHTS);
  return (
    <div
      className="a-spacer"
      style={{
        width: w,
        height: h,
        flex: width === 'full' || height === 'full' ? 1 : undefined,
      }}
    />
  );
}

// ─── ColorBlock ────────────────────────────────────────────────────────

export function ArtifactColorBlock({
  color = 'var(--artifact-muted)',
  width = 'medium',
  height = 'medium',
}: {
  color?: string;
  width?: string | number;
  height?: string | number;
}) {
  const w = resolveSizeSpec(width, SPACER_WIDTHS);
  const h = resolveSizeSpec(height, SPACER_HEIGHTS);
  return (
    <div
      className="a-color-block"
      style={{
        width: w,
        height: h,
        flex: width === 'full' || height === 'full' ? 1 : undefined,
        background: parseColor(color),
        borderRadius: 'var(--artifact-border-radius)',
      }}
    />
  );
}

// ─── Floating ──────────────────────────────────────────────────────────

const INSIDE_POS: Record<string, React.CSSProperties> = {
  north:      { position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)' },
  north_east: { position: 'absolute', top: 8, right: 8 },
  east:       { position: 'absolute', top: '50%', right: 8, transform: 'translateY(-50%)' },
  south_east: { position: 'absolute', bottom: 8, right: 8 },
  south:      { position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)' },
  south_west: { position: 'absolute', bottom: 8, left: 8 },
  west:       { position: 'absolute', top: '50%', left: 8, transform: 'translateY(-50%)' },
  north_west: { position: 'absolute', top: 8, left: 8 },
};

export function ArtifactFloating(p: CompassProps) {
  return (
    <div style={{ position: 'relative' }}>
      <Slot style={{ display: 'flex', flexDirection: 'column', gap: 'var(--artifact-gap, 16px)' }}>
        {p.center ?? []}
      </Slot>
      <Slot style={INSIDE_POS.north}>{p.north ?? []}</Slot>
      <Slot style={INSIDE_POS.north_east}>{p.north_east ?? []}</Slot>
      <Slot style={INSIDE_POS.east}>{p.east ?? []}</Slot>
      <Slot style={INSIDE_POS.south_east}>{p.south_east ?? []}</Slot>
      <Slot style={INSIDE_POS.south}>{p.south ?? []}</Slot>
      <Slot style={INSIDE_POS.south_west}>{p.south_west ?? []}</Slot>
      <Slot style={INSIDE_POS.west}>{p.west ?? []}</Slot>
      <Slot style={INSIDE_POS.north_west}>{p.north_west ?? []}</Slot>
    </div>
  );
}

